import { useAsync } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import Cookies from "js-cookie";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useEffect, useMemo, useRef } from "react";
import { fetchQuery, graphql, useFragment, useRelayEnvironment } from "react-relay";
import { createOperationDescriptor, getRequest } from "relay-runtime";
import type { Disposable } from "relay-runtime";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { accountAtom } from "shared/lib/account/atoms";
import { getAbsoluteUrl, notEmpty } from "shared/lib/utils";

import { useTracking } from "scmp-app/components/article/article-recirculation-widget/hooks";
import { fetchArticlePageRecommendation } from "scmp-app/lib/ai-recommendation/article-page/api";
import { appAtom } from "scmp-app/lib/app/atoms";
import { useUserService } from "scmp-app/lib/user-service/hooks";
import type { topicYouMayLikeWidgetArticle$key } from "scmp-app/queries/__generated__/topicYouMayLikeWidgetArticle.graphql";
import type {
  topicYouMayLikeWidgetQuery,
  topicYouMayLikeWidgetQuery$variables,
} from "scmp-app/queries/__generated__/topicYouMayLikeWidgetQuery.graphql";
import { FollowType } from "scmp-app/scmp-sdk";

import {
  Container,
  StyledContentItemArticleWidget,
  StyledHomeFollowButton,
  Title,
  TopicContainer,
  TopicName,
} from "./styles";

type Props = {
  className?: string;
  reference: topicYouMayLikeWidgetArticle$key;
};

export const ArticleTopicYouMayLikeWidget: FunctionComponent<Props> = ({
  className,
  reference,
}) => {
  const article = useFragment(
    graphql`
      fragment topicYouMayLikeWidgetArticle on Article {
        entityId
      }
    `,
    reference,
  );

  const environment = useRelayEnvironment();
  const disposableReference = useRef<Disposable | null>(null);
  const { navigatorInfo, navigatorInfoLoaded } = useAtomValue(appAtom);
  const { user } = useAtomValue(accountAtom);

  const [{ result, status }, { execute }] = useAsync(async () => {
    const reply = await fetchArticlePageRecommendation({
      excludeArticleIds: [article.entityId],
      reqRegion: navigatorInfo?.country ?? "GLOBAL",
      userId: user?.uuid,
      visitorId: Cookies.get("_vi") ?? "",
    });
    const recommendationId = reply?.requestId ?? "";
    const topicId = first(reply?.recommendationArea.widgets)?.topic?.id ?? "";
    const articleIds =
      first(reply?.recommendationArea.widgets)
        ?.items?.toSorted((a, b) => a.slotId - b.slotId)
        ?.map(({ article }) => article.articleId) ?? [];

    const query = graphql`
      query topicYouMayLikeWidgetQuery(
        $hasArticle: Boolean!
        $hasTopic: Boolean!
        $topicId: String!
        $articleIds: [String!]
      ) {
        topic(filter: { entityId: $topicId }) @include(if: $hasTopic) {
          name
          entityId
          disableFollow
          ...entityLink
          ...followButtonBase
        }
        articles(filter: { entityIds: $articleIds }) @include(if: $hasArticle) {
          edges {
            node {
              entityId
              ...articleWidgetContent
            }
          }
        }
      }
    `;
    const variables: topicYouMayLikeWidgetQuery$variables = {
      articleIds,
      hasArticle: notEmpty(articleIds),
      hasTopic: notEmpty(topicId),
      topicId,
    };
    const queryRequest = getRequest(query);
    const queryDescriptor = createOperationDescriptor(queryRequest, variables);
    disposableReference.current = environment.retain(queryDescriptor);

    const data = await fetchQuery<topicYouMayLikeWidgetQuery>(environment, query, variables, {
      fetchPolicy: "store-or-network",
    }).toPromise();

    return {
      articleIds,
      data,
      recommendationId,
    };
  });

  useEffect(() => {
    if (status !== "not-executed" || !navigatorInfoLoaded) return;
    void execute();

    return () => {
      disposableReference.current?.dispose();
    };
  }, [execute, navigatorInfoLoaded, status]);

  const { articleIds = [], data, recommendationId } = result ?? {};
  const topic = useMemo(() => data?.topic, [data?.topic]);
  const items = useMemo(() => {
    const nodes = data?.articles?.edges?.map(({ node }) => node) ?? [];
    return articleIds.map(id => nodes.find(({ entityId }) => entityId === id)).filter(notEmpty);
  }, [articleIds, data?.articles?.edges]);

  const { checkIsFollowed } = useUserService();
  const isTopicFollowed = useMemo(() => {
    if (!topic) return false;
    return checkIsFollowed(FollowType.Topic, topic.entityId);
  }, [checkIsFollowed, topic]);

  const { captureImpressionTrackingElement, handleClickTracking } = useTracking({
    getClickCustomizedParameters: (_entityId, urlAlias) => ({
      article_id: article.entityId,
      block_section: topic?.name ?? "",
      destination_url: getAbsoluteUrl(urlAlias),
      list_of_articles: items.map(node => node.entityId).join(","),
      page_type: "article",
      recommendation_id: recommendationId ?? "",
      widget_name: "feature_package",
    }),
    getImpressionCustomizedParameters: () => ({
      block_section: topic?.name ?? "",
      list_of_articles: items.map(node => node.entityId).join(","),
      page_type: "article",
      recommendation_id: recommendationId ?? "",
      widget_name: "feature_package",
    }),
  });

  if (!notEmpty(items)) return null;
  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "topic_you_may_like",
        pgtype: "article",
      }}
    >
      <Container className={className}>
        <Title ref={captureImpressionTrackingElement}>Topic You May Like</Title>
        {notEmpty(topic) && (
          <TopicContainer>
            <TopicName reference={topic}>{topic.name}</TopicName>
            {!topic.disableFollow && (
              <StyledHomeFollowButton
                $isFollowed={isTopicFollowed}
                isLoadingEnabled={true}
                reference={topic}
                source="topic_you_may_like"
                type={FollowType.Topic}
              />
            )}
          </TopicContainer>
        )}
        {items?.map(node => (
          <StyledContentItemArticleWidget
            key={node.entityId}
            onClick={handleClickTracking}
            reference={node}
          />
        ))}
      </Container>
    </BaseLinkContextProvider>
  );
};

ArticleTopicYouMayLikeWidget.displayName = "ArticleTopicYouMayLikeWidget";
